
import { defineComponent } from 'vue'
import { mapGetters, mapState } from 'vuex'
import AsahiProgress from '@/components/library/AsahiProgress.vue'
import AllertCart from '@/components/library/assets/AllertCart.vue'
import SuccessCart from '@/components/library/assets/SuccessCart.vue'
import formatDateMixin from '@/utils/mixin'
import { constants } from '@/constants'

const HOMEPAGE_URL = '/'

export default defineComponent({
  components: { SuccessCart, AllertCart, AsahiProgress },
  mixins: [formatDateMixin],
  props: {
    changeMessageColor: {
      type: Boolean,
      default: false
    },
    messageType: {
      type: String,
      default: 'header'
    },
    mobile: {
      type: Boolean,
      default: false
    },
    isWhite: {
      type: Boolean,
      default: false
    },
    isCart: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      errorWeight: false,
      successWeight: false,
      isComponentVisible: false,
      errorWeightBar: false,
      isMobile: (window.innerWidth <= 768) || (this.messageType === 'mobile'),
      customerRoleType: constants.customertype,
      mobileCart: 'mobile-cart'
    }
  },
  computed: {
    ...mapState('basket', ['id', 'query', 'items', 'paymentMethod']),
    ...mapGetters('basket', ['progressBarSuccess', 'showProgressBarMessage']),
    ...mapState('catalog', ['alignment']),

    progressBar () {
      return this.query?.progress_bar_data
    },

    isHomePge () {
      return this.$route.path === HOMEPAGE_URL
    },
    maxOrderCash () {
      return this.progressBar?.iMaximumOrderCash ?? 0
    },
    grandTotalYourPrice () {
      return this.query?.grand_total_your_price_details?.grand_total_your_price ?? 0
    },
    cashFilledPercentage () {
      return (this.grandTotalYourPrice / this.maxOrderCash) * 100
    },
    differenceResult () {
      return Math.round(this.maxOrderCash - this.grandTotalYourPrice)
    },
    totalCartPallet () {
      return this.progressBar?.iTotalCartPalletQty ?? 0
    },
    minimumOrderPallett () {
      return this.progressBar?.iMinimumOrderPallet ?? 0
    },
    minimumOrderUnits () {
      return this.progressBar?.iMinimumUnit ?? 0
    },
    maximumOrderUnits () {
      return this.progressBar?.iMaximumUnit ?? 0
    },
    minimumOrderWeight () {
      return this.progressBar?.iMinimumOrderWeight ?? 0
    },
    maximumOrderWeight () {
      return this.progressBar?.iMaximumOrderWeight ?? 0
    },
    totalCartUnits () {
      return this.progressBar?.iTotalQty ?? 0
    },
    totalCartWeight () {
      return this.progressBar?.iTotalCartWeight ?? 0
    },

    messageColorClass () {
      if (this.changeMessageColor) {
        if (this.progressBarMessage.hasError) {
          return 'border-error-text'
        } else if (this.progressBarMessage.hasSucccess) {
          return 'border-success-text'
        } else {
          return 'border-default-text'
        }
      }
      return 'border-default-text'
    },

    isCompactAndFullTruck () {
      return this.messageType === 'compact' && this.progressBar?.isFullTruck === true
    },
    isCompactAndNotFullTruck () {
      return this.messageType === 'compact' && this.progressBar?.isFullTruck === false
    },

    progressBarMessage () {
      if (this.isPosHorecaUser) {
        const kegFlagYes = this.progressBar?.iKegFlag === 'yes'
        const minUnit = kegFlagYes ? 1 : this.minimumOrderUnits

        if (this.progressBar?.iTotalQty <= 0) {
          return {
            title: `${this.$t('Minimum order capacity not reached')}`,
            subtitle: `${this.$t('The minimum number of units has not been reached, please add more goods.')}`,
            buttonTitle: this.$t('Min units', { n: minUnit }),
            hasError: false,
            hasSucccess: false
          }
        }

        if (this.progressBar?.iTotalQty < minUnit) {
          return {
            title: `${this.$t('Minimum order capacity not reached')}`,
            subtitle: `${this.$t('The minimum number of units has not been reached, please add more goods.')}`,
            buttonTitle: this.$t('Min units', { n: minUnit }),
            hasError: true,
            hasSucccess: false
          }
        }

        if (this.progressBar?.iTotalQty > this.progressBar?.iMaximumUnit) {
          return {
            title: this.isCart
              ? `${this.$t('Maximum capacity exceeded cart for pos')}`
              : `${this.$t('Maximum capacity exceeded')}`,
            subtitle: `${this.$t('The number of units has been exceeded.')}`,
            buttonTitle: `${this.$t('Max units', { n: this.maximumOrderUnits })}`,
            hasError: true,
            hasSucccess: false
          }
        }

        return {
          title: this.isMobile
            ? `${this.$t('Unit reached')}`
            : `${this.$t('Minimum order capacity reached')}`,
          subtitle: `${this.$t('The required quantity of goods has been reached.')}`,
          buttonTitle: '',
          hasError: false,
          hasSucccess: true
        }
      } else if (this.isPosFoodUser) {
        if (this.progressBar?.iTotalQty <= 0) {
          return {
            title: `${this.$t('Minimum order capacity not reached')}`,
            subtitle: `${this.$t('The minimum number of units has not been reached, please add more goods.')}`,
            buttonTitle: this.$t('Min units', { n: this.minimumOrderUnits }),
            hasError: false,
            hasSucccess: false
          }
        } else if (this.progressBar?.iTotalQty > this.progressBar?.iMaximumUnit) {
          return {
            title: `${this.$t('Maximum capacity exceeded')}`,
            subtitle: `${this.$t('The number of units has been exceeded.')}`,
            buttonTitle: `${this.$t('Max units', { n: this.maximumOrderUnits })}`,
            hasError: true,
            hasSucccess: false
          }
        } else if (this.progressBar?.iTotalQty < this.progressBar?.iMinimumUnit) {
          return {
            title: `${this.$t('Minimum order capacity not reached')}`,
            subtitle: `${this.$t('The minimum number of units has not been reached, please add more goods.')}`,
            buttonTitle: this.$t('Min units', { n: this.minimumOrderUnits }),
            hasError: true,
            hasSucccess: false
          }
        } else {
          return {
            title: this.isMobile ? `${this.$t('Unit reached')}` : `${this.$t('Minimum order capacity reached')}`,
            subtitle: `${this.$t('The required quantity of goods has been reached.')}`,
            buttonTitle: '',
            hasError: false,
            hasSucccess: true
          }
        }
      } else if (this.isWHSUser) {
        // no items in the cart
        if (this.progressBar?.iTotalCartPalletQty <= 0) {
          if (this.isCart) {
            return {
              title: `${this.$t('Minimum order capacity not reached for whs')}`,
              subtitle: `${this.$t('The minimum number of pallet has not been reached, please add more goods.')}`,
              buttonTitle: this.$t('Min Pallets', { n: this.minimumOrderPallett }),
              hasError: false,
              hasSucccess: false
            }
          } else {
            return {
              title: `${this.$t('Minimum order capacity not reached for whs')}`,
              subtitle: `${this.$t('The minimum number of pallets has not yet been reached, please add more goods.')}`,
              buttonTitle: this.$t('Min Pallets', { n: this.minimumOrderPallett }),
              hasError: false,
              hasSucccess: false
            }
          }
        } else if (this.progressBar?.iTotalCartPalletQty < this.progressBar?.iMinimumOrderPallet) {
          return {
            title: `${this.$t('Minimum order capacity not reached for whs')}`,
            subtitle: `${this.$t('The minimum number of pallet has not been reached, please add more goods.')}`,
            buttonTitle: this.$t('Min Pallets', { n: this.minimumOrderPallett }),
            hasError: true,
            hasSucccess: false
          }
        } else if (this.progressBar?.iTotalCartWeight > this.progressBar?.iMaximumOrderWeight) {
          if (this.isCart) {
            return {
              title: `${this.$t('Maximum capacity exceeded cart for whs')}`,
              subtitle: `${this.$t('The number of pallets has been exceeded')}`,
              buttonTitle: this.$t('Max weight', { n: this.progressBar.iMaximumOrderWeight }),
              hasError: true,
              hasSucccess: false
            }
          } else {
            return {
              title: `${this.$t('Maximum capacity exceeded for whs')}`,
              subtitle: `${this.$t('The weight of the order exceeds the limit, reduce the quantity to proceed with the order.')}`,
              buttonTitle: this.$t('Max weight', { n: this.progressBar.iMaximumOrderWeight }),
              hasError: true,
              hasSucccess: false
            }
          }
        } else {
          return {
            title: `${this.$t('Minimum order capacity reached for whs')}`,
            subtitle: `${this.$t('The required quantity of goods has been reached.')}`,
            buttonTitle: '',
            hasError: false,
            hasSucccess: true
          }
        }
      }
      return { title: '', subtitle: '' }
    },
    progressBarMiniMessage () {
      const shouldHideExpandedTitle = this.isPosHorecaUser && this.progressBar?.iKegFlag === 'yes'

      if (!this.isWHSUser) {
        if (this.totalCartUnits <= 0) {
          const unitDiff = this.minimumOrderUnits - this.totalCartUnits
          return {
            title: `${this.$t('Add minimum', { n: unitDiff })}`,
            expendedTitle: shouldHideExpandedTitle ? '' : `<span class='fs-10 fw-regular'>${this.$t('Reach minimum order capacity')}:</span> ${unitDiff} ${this.$t('units')}`,
            hasError: false,
            hasSucccess: false
          }
        } else if (this.totalCartUnits < this.minimumOrderUnits) {
          const unitDiff = this.minimumOrderUnits - this.totalCartUnits
          return {
            title: `${this.$t('Add more', { n: unitDiff })}`,
            expendedTitle: shouldHideExpandedTitle ? '' : `${this.$t('Add more expanded', { n: unitDiff })}`,
            hasError: true,
            hasSucccess: false
          }
        } else if (this.totalCartUnits > this.maximumOrderUnits) {
          return {
            title: `${this.$t('exceeded maximum capacity')}`,
            expendedTitle: shouldHideExpandedTitle ? '' : `<span class='fs-10 fw-regular'>${this.$t('Exceeded maximum order capacity')}:</span> ${this.totalCartUnits} ${this.$t('units_2')}`,
            hasError: true,
            hasSucccess: false
          }
        } else {
          return {
            title: `${this.$t('units added', { n: this.totalCartUnits })}`,
            expendedTitle: shouldHideExpandedTitle ? '' : `${this.$t('units added', { n: this.totalCartUnits })} <span class='fs-10 fw-regular'>${this.$t('minimum order capacity reached')}<span>`,
            hasError: false,
            hasSucccess: true
          }
        }
      } else if (this.isWHSUser) {
        if (this.totalCartPallet <= 0) {
          return {
            title: `${this.$t('Add minimum pallets', { n: this.minimumOrderPallett })}`,
            expendedTitle: shouldHideExpandedTitle ? '' : `<span class='fs-10 fw-regular'>${this.$t('Reach minimum number of pallets:')}</span> ${this.totalCartPallet} / ${this.minimumOrderPallett}`,
            hasError: false,
            hasSucccess: false
          }
        } else if (this.totalCartPallet < this.minimumOrderPallett) {
          const unitDiff = this.minimumOrderPallett - this.totalCartPallet
          return {
            title: `${this.$t('Add more pallets', { n: unitDiff })}`,
            expendedTitle: shouldHideExpandedTitle ? '' : `<span class='fs-10 fw-regular'>${this.$t('Reach minimum number of pallets:')}</span> ${this.totalCartPallet} / ${this.minimumOrderPallett}`,
            hasError: true,
            hasSucccess: false
          }
        } else if (this.totalCartPallet > this.minimumOrderPallett && this.totalCartWeight > this.maximumOrderWeight) {
          return {
            title: `${this.$t('Exceeded maximum weight')}`,
            expendedTitle: shouldHideExpandedTitle ? '' : `<span class='fs-10 fw-regular'>${this.$t('Exceeded maximum weight')}:</span> ${parseInt(this.totalCartWeight)} Kg / ${this.maximumOrderWeight} Kg`,
            hasError: true,
            hasSucccess: false
          }
        } else {
          return {
            title: `${this.$t('pallets added', { n: this.totalCartPallet })}`,
            expendedTitle: shouldHideExpandedTitle ? '' : `<span class='fs-10 fw-regular'>${this.$t('Reached minimum order capacity:')}</span> ${parseInt(this.totalCartWeight)} Kg / ${this.minimumOrderWeight} Kg`,
            hasError: false,
            hasSucccess: true
          }
        }
      }

      return { title: '', subtitle: '' }
    },
    user () {
      return this.$store.state.auth.user
    },
    isWHSUser () {
      const customerRoleType = constants.customertype
      return this.user?.permissionSchema?.some((company) => company.rpDetails?.some((rp) => rp.customerType === customerRoleType.WHS))
    },
    isPosHorecaUser () {
      return this.progressBar?.iCustomerType === this.customerRoleType.POS_HORECA
    },
    isPosFoodUser () {
      return this.progressBar?.iCustomerType === this.customerRoleType.POS_FOOD
    }
  },
  methods: {
    getFillPercentage () {
      let fillPercentage = 0
      if (!this.isWHSUser) {
        const currentValue = this.progressBarMessage.hasSucccess ? this.minimumOrderUnits : this.totalCartUnits
        fillPercentage = currentValue / this.minimumOrderUnits * 100
      } else {
        const currentValue = this.progressBarMessage.hasSucccess ? this.maximumOrderWeight : this.totalCartWeight
        fillPercentage = currentValue / this.maximumOrderWeight * 100
      }
      this.emitRequirementStatus()
      return fillPercentage
    },
    emitRequirementStatus () {
      const requirementMessage = this.progressBarMessage.buttonTitle
      this.$emit('requirement-status', requirementMessage)
    },
    emptiesDeliveryMethod () {
      const deliveryMethod = localStorage.getItem('shippingMethod')
      if (deliveryMethod === 'emptiespickup_emptiespickup') {
        return true
      }
    }
  }
})
